/* eslint-disable security/detect-object-injection */
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface ISearchableDropdownOption {
    [key: string]: string;
}

interface ISearchableDropdownProps {
    options: ISearchableDropdownOption[];
    label: string;
    id: string;
    selectedVal: string | null;
    handleChange: (value: string | null) => void;
}

const SearchableDropdown: React.FC<ISearchableDropdownProps> = ({ options, label, id, selectedVal, handleChange }) => {
    const [query, setQuery] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const CLASS_NAME = 'lxs-searchable-dropdown';

    // Display logic:
    // - If the user has typed (query !== ''), show the query.
    // - If the dropdown is open and query is empty, show an empty input (prevents re-populating with selectedVal).
    // - If the dropdown is closed and query is empty, show the selected value (so the control reflects the selection).
    const getDisplayValue = (): string => {
        if (query !== '') {
            return query;
        }
        if (isOpen) {
            return '';
        }
        return selectedVal ?? '';
    };

    const filterOptions = useMemo(
        () => (opts: ISearchableDropdownOption[]) => opts.filter(option => option[label].toLowerCase().includes(query.toLowerCase())),
        [label, query]
    );

    const toggle = () => {
        setIsOpen(prev => !prev);
        if (!isOpen) {
            setHoveredIndex(null);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setQuery(val);
        if (val !== '') {
            handleChange(null);
        }
    };

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
        setIsOpen(true);
    };

    const selectOption = (option: ISearchableDropdownOption) => {
        setQuery('');
        handleChange(option[label]);
        setIsOpen(false);
        setHoveredIndex(null);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setHoveredIndex(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
        if (!isOpen) {
            return;
        }
        const filtered = filterOptions(options);
        const maxIndex = filtered.length - 1;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHoveredIndex(prev => (prev === null || prev >= maxIndex ? 0 : prev + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHoveredIndex(prev => (prev === null || prev <= 0 ? maxIndex : prev - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (hoveredIndex !== null && filtered[hoveredIndex]) {
                selectOption(filtered[hoveredIndex]);
            }
        }
    };

    return (
        <div className={CLASS_NAME} ref={dropdownRef}>
            <div className={`${CLASS_NAME}__control`}>
                <div className={`${CLASS_NAME}__control-selected-value`}>
                    <input
                        ref={inputRef}
                        className={`${CLASS_NAME}__input`}
                        type='text'
                        value={getDisplayValue()}
                        name='searchTerm'
                        onChange={handleInputChange}
                        onClick={() => {
                            setIsOpen(true);
                        }}
                        onFocus={handleInputFocus}
                        onKeyDown={handleKeyDown}
                        aria-haspopup='listbox'
                        aria-expanded={isOpen}
                        role='combobox'
                        autoComplete='off'
                    />
                </div>
                <div
                    className={[`${CLASS_NAME}__arrow`, isOpen ? `${CLASS_NAME}__arrow--open` : ''].filter(Boolean).join(' ')}
                    onClick={toggle}
                    role='button'
                    tabIndex={0}
                    aria-label={isOpen ? 'Close options' : 'Open options'}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle();
                        }
                    }}
                />
            </div>
            {isOpen && (
                <div
                    className={[`${CLASS_NAME}__options`, `${CLASS_NAME}__options--open`].join(' ')}
                    role='listbox'
                    aria-activedescendant=''
                >
                    {filterOptions(options).map((option, index) => {
                        const isHovered = index === hoveredIndex;
                        const isSelected = option[label] === selectedVal;
                        return (
                            <div
                                key={`${id}-${index}`}
                                onClick={() => selectOption(option)}
                                onMouseEnter={() => setHoveredIndex(index)}
                                className={[
                                    `${CLASS_NAME}__option`,
                                    isSelected ? `${CLASS_NAME}__option--selected` : '',
                                    isHovered ? `${CLASS_NAME}__option--hovered` : ''
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                role='option'
                                aria-selected={isSelected}
                                tabIndex={-1}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        selectOption(option);
                                    }
                                }}
                            >
                                {option[label]}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
